import { salaryInsightsService } from '../../../services/salaryInsightsService';

export const SalaryWidget = ({ 
  salaryData, 
  loading, 
  occupation, 
  zipCode, 
  experienceLevel, 
  userRole 
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="bg-white rounded-lg shadow p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!salaryData || salaryData?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
          <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.348 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Salary Data Available</h3>
        <p className="text-gray-600">
          Unable to find salary information for {occupation?.title} in ZIP code {zipCode}. 
          Try a different location or check back later.
        </p>
      </div>
    );
  }

  // Group data by area type
  const groupedData = salaryData?.reduce((acc, item) => {
    acc[item.area_type] = item;
    return acc;
  }, {});

  const nationalData = groupedData?.national;
  const stateData = groupedData?.state;
  const msaData = groupedData?.msa || groupedData?.zip;

  const getSalaryCard = (data, title, description, isPremium = false) => {
    const isBlocked = isPremium && userRole === 'free';
    
    return (
      <div className={`bg-white rounded-lg shadow p-6 relative ${isBlocked ? 'opacity-60' : ''}`}>
        {isBlocked && (
          <div className="absolute inset-0 bg-gray-50/80 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="text-sm font-medium text-gray-900">Premium Feature</p>
              <p className="text-xs text-gray-600">Upgrade to unlock</p>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {isPremium && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Premium
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        {data && !isBlocked ? (
          <div className="space-y-3">
            {/* Salary Range */}
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Range</span>
                <span>
                  {salaryInsightsService?.formatSalary(data?.min_salary)} - {salaryInsightsService?.formatSalary(data?.max_salary)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full"
                  style={{ width: '75%' }}
                ></div>
              </div>
            </div>
            
            {/* Average */}
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {salaryInsightsService?.formatSalary(data?.avg_salary)}
              </div>
              <div className="text-sm text-gray-600">Average {experienceLevel} salary</div>
            </div>
            
            {/* Comparison with national (if not national data) */}
            {data?.area_type !== 'national' && nationalData && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                {(() => {
                  const comparison = salaryInsightsService?.calculateComparison(
                    data?.avg_salary,
                    nationalData?.avg_salary
                  );
                  
                  if (!comparison) return null;
                  
                  return (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">vs. National</span>
                      <span className={`font-medium ${comparison?.isHigher ? 'text-green-600' : comparison?.isLower ? 'text-red-600' : 'text-gray-600'}`}>
                        {comparison?.isHigher ? '+' : ''}{comparison?.percentage}%
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        ) : !isBlocked && (
          <div className="text-center py-8 text-gray-500">
            <svg className="mx-auto h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No data available</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          {occupation?.title} Salary Insights
        </h2>
        <p className="text-gray-600">
          {experienceLevel?.charAt(0)?.toUpperCase() + experienceLevel?.slice(1)} level salaries for ZIP code {zipCode}
        </p>
      </div>
      {/* Salary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Local/MSA Data (Premium) */}
        {getSalaryCard(
          msaData,
          'Local Area',
          msaData ? `Metro/Local area around ${zipCode}` : 'Local area data',
          true
        )}

        {/* State Data */}
        {getSalaryCard(
          stateData,
          'State Average',
          stateData ? `State-wide salary data` : 'State salary data',
          false
        )}

        {/* National Data */}
        {getSalaryCard(
          nationalData,
          'National Average',
          'United States national average',
          false
        )}
      </div>
      {/* Data Source Footer */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center">
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Data provided by CareerOneStop</span>
          </div>
          {salaryData?.[0]?.fetched_at && (
            <span>
              Updated: {new Date(salaryData[0].fetched_at)?.toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};